import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "@i18n";
import PurchaseOrderCard from "./PurchaseOrderCard";
import Loader from "@componentsShared/Loader/Loader";
import TrackingEmptyState from "./TrackingEmptyState";
import TrackingLandingHeader from "./TrackingLandingHeader";
import Layout from "@/_LayoutsRelated/layout/Layout";
import { Icon } from "@syarahtech/ui";
import { useLang } from "@/common/Contexts/LanguageContext";
import useHelpers from "@hooks/useHelpers";
import useApi from "@hooks/useApi";
import { trackEvents } from "@/common/events/events";
import { api__getOrderTrackingTabs } from "@/apis/orderTracking/api__getOrderTrackingTabs";
import useWebView from "@hooks/useWebview";
import emptyCarsImg from "./assets/empty_cars.svg";
import emptyServiceImg from "./assets/empty_service.svg";
var createDetailsPageUrl = function createDetailsPageUrl(order) {
  var url = order.is_finance ? "/tracking-finance-details/" : "/tracking-checkout-details/";
  return url + order.id;
};
var tabToCategory = function tabToCategory(tabKey) {
  return tabKey === "car_services" ? "services" : tabKey === "sell_orders" ? "sell" : "purchase";
};
var ServiceBadge = function ServiceBadge(_ref) {
  var order = _ref.order,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? "" : _ref$className;
  return /*#__PURE__*/React.createElement("div", {
    className: "inline-flex items-center gap-1 rounded-[12px] ps-2 pe-3 py-0.5 text-[10px] font-bold leading-4 whitespace-nowrap ".concat(className),
    style: {
      backgroundColor: order.badge.text_background,
      color: order.badge.text_color
    }
  }, /*#__PURE__*/React.createElement("img", {
    src: order.badge.icon,
    alt: "",
    className: "w-4 h-4 shrink-0"
  }), /*#__PURE__*/React.createElement("span", null, order.badge.text));
};
var CarServiceCard = function CarServiceCard(_ref2) {
  var order = _ref2.order,
    link = _ref2.link;
  return /*#__PURE__*/React.createElement(Link, {
    to: link,
    className: "flex items-center gap-3 p-3 bg-white border border-solid border-[#d6dfe9] rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)]"
  }, /*#__PURE__*/React.createElement("div", {
    className: "shrink-0 w-[84px] h-[68px] lg:w-[120px] lg:h-[106px] rounded-lg overflow-hidden bg-[#f2f2f2]"
  }, /*#__PURE__*/React.createElement("img", {
    src: order.product_image,
    alt: order.title,
    className: "w-full h-full object-contain"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex-1 min-w-0 flex flex-col text-right gap-1"
  }, /*#__PURE__*/React.createElement("div", {
    className: "text-sm font-bold leading-6 text-[#00143c] truncate w-full"
  }, order.title), /*#__PURE__*/React.createElement("div", {
    className: "text-xs font-normal leading-4",
    style: {
      color: order.display_status_color
    }
  }, order.display_status), /*#__PURE__*/React.createElement("div", {
    className: "text-[10px] leading-4 text-[#6c7a8d]"
  }, order.created_at_formatted), /*#__PURE__*/React.createElement(ServiceBadge, {
    order: order,
    className: "mt-1 self-start lg:hidden py-1"
  })), /*#__PURE__*/React.createElement(ServiceBadge, {
    order: order,
    className: "hidden lg:inline-flex shrink-0 py-1 text-xs"
  }), /*#__PURE__*/React.createElement("div", {
    className: "shrink-0 flex items-center ltr:rotate-180"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: "material-symbols--arrow-back-ios-rounded",
    className: "text-primary-new",
    width: 4,
    height: 4
  })));
};
var TrackingLandingTabs = function TrackingLandingTabs() {
  var _data$tabs$find2, _data$purchase_orders3, _data$car_services2, _data$purchase_orders4, _data$car_services3;
  var _useTranslation = useTranslation(["web_tracking_finance", "web_tracking"]),
    t = _useTranslation.t;
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl;
  var lang = useLang();
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  var webview = useWebView();
  var _useApi = useApi([], function () {
      return api__getOrderTrackingTabs();
    }),
    _useApi2 = _slicedToArray(_useApi, 3),
    loading = _useApi2[0],
    data = _useApi2[2];

  // order_tracking_entry — direct-link landing (WhatsApp/SMS, etc.)
  useEffect(function () {
    if (!__IS_CSR__) return;
    var isDirectLink = !document.referrer || !document.referrer.includes(window.location.hostname);
    if (isDirectLink) {
      trackEvents("order_tracking_entry", {
        referral_location: "direct_link"
      });
    }
  }, []);

  // order_tracking_landing — fires per tab list shown
  useEffect(function () {
    var _data$tabs$find, _data$car_services, _data$car_services$or, _data$purchase_orders, _data$purchase_orders2;
    if (loading || !data) return;
    var defaultKey = ((_data$tabs$find = data.tabs.find(function (tab) {
      return tab.is_default;
    })) === null || _data$tabs$find === void 0 ? void 0 : _data$tabs$find.key) || "purchase_orders";
    var urlTab = searchParams.get("tab");
    var tabKey = urlTab && data.tabs.some(function (tab) {
      return tab.key === urlTab;
    }) ? urlTab : defaultKey;
    var count = tabKey === "car_services" ? ((_data$car_services = data.car_services) === null || _data$car_services === void 0 ? void 0 : (_data$car_services$or = _data$car_services.orders) === null || _data$car_services$or === void 0 ? void 0 : _data$car_services$or.length) || 0 : ((_data$purchase_orders = data.purchase_orders) === null || _data$purchase_orders === void 0 ? void 0 : (_data$purchase_orders2 = _data$purchase_orders.orders) === null || _data$purchase_orders2 === void 0 ? void 0 : _data$purchase_orders2.length) || 0;
    trackEvents("order_tracking_landing", {
      number_of_orders: count,
      tracking_category: tabToCategory(tabKey)
    });
  }, [loading, data, searchParams]);
  if (loading || !data) return /*#__PURE__*/React.createElement(Loader, null);
  var defaultTabKey = ((_data$tabs$find2 = data.tabs.find(function (tab) {
    return tab.is_default;
  })) === null || _data$tabs$find2 === void 0 ? void 0 : _data$tabs$find2.key) || "purchase_orders";
  var urlTab = searchParams.get("tab");
  var resolvedTab = urlTab && data.tabs.some(function (tab) {
    return tab.key === urlTab;
  }) ? urlTab : defaultTabKey;
  var selectTab = function selectTab(key) {
    return setSearchParams({
      tab: key
    }, {
      replace: true
    });
  };
  var purchaseOrders = ((_data$purchase_orders3 = data.purchase_orders) === null || _data$purchase_orders3 === void 0 ? void 0 : _data$purchase_orders3.orders) || [];
  var carServiceOrders = ((_data$car_services2 = data.car_services) === null || _data$car_services2 === void 0 ? void 0 : _data$car_services2.orders) || [];
  var renderEmptyState = function renderEmptyState(emptyState, image) {
    if (!emptyState) return null;
    var cta_link = emptyState.cta_link,
      cta_deep_link = emptyState.cta_deep_link;
    var hasCta = Boolean(cta_link || cta_deep_link);
    return /*#__PURE__*/React.createElement("div", {
      className: "py-6"
    }, /*#__PURE__*/React.createElement(TrackingEmptyState, {
      title: emptyState.title,
      subtitle: emptyState.subtitle,
      buttonText: emptyState === null || emptyState === void 0 ? void 0 : emptyState.cta_label,
      image: image,
      onButtonClick: hasCta ? function () {
        if (cta_deep_link) {
          webview.openDeepLink(cta_deep_link)["catch"](function () {
            if (cta_link) window.location.href = cta_link;
          });
        } else if (cta_link) {
          window.location.href = cta_link;
        }
      } : undefined
    }));
  };
  return /*#__PURE__*/React.createElement(Layout, {
    linksForSearch: true,
    mobileHasTitle: t("track_orders", {
      ns: "web_tracking_finance"
    }),
    hideOrangeHeadBtn: true,
    mobileHasBack: true,
    mobileNoLogo: true,
    headHideLinks: true,
    hideHeaderOnmobile: true,
    authenticationMsg: true,
    hideFooterWithoutAuthMsg: true
  }, /*#__PURE__*/React.createElement(TrackingLandingHeader, {
    title: t("track_orders")
  }), /*#__PURE__*/React.createElement("div", {
    className: "max-w-[980px] mx-auto w-[90%] lg:w-full md:mt-14"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center mt-6"
  }, data.tabs.map(function (tab) {
    return /*#__PURE__*/React.createElement("button", {
      key: tab.key,
      onClick: function onClick() {
        return selectTab(tab.key);
      },
      className: "flex-1 flex flex-col gap-1 items-center bg-transparent border-0 p-0 cursor-pointer"
    }, /*#__PURE__*/React.createElement("span", {
      className: "text-[16px] leading-6 transition-colors ".concat(resolvedTab === tab.key ? "font-bold text-primary-new" : "font-normal text-[#00143c]")
    }, tab.label), /*#__PURE__*/React.createElement("span", {
      className: "h-0.5 w-full transition-colors ".concat(resolvedTab === tab.key ? "bg-primary-new" : "bg-[#E6E6E6]")
    }));
  })), resolvedTab === "purchase_orders" && (purchaseOrders.length === 0 ? renderEmptyState((_data$purchase_orders4 = data.purchase_orders) === null || _data$purchase_orders4 === void 0 ? void 0 : _data$purchase_orders4.empty_state, emptyCarsImg) : /*#__PURE__*/React.createElement("div", {
    className: "py-6"
  }, /*#__PURE__*/React.createElement("div", {
    className: "bg-white rounded-2xl drop-shadow-[0px_1px_1px_rgba(0,0,0,0.08)] p-4 lg:p-10 flex flex-col gap-4 lg:gap-6"
  }, purchaseOrders.map(function (order, idx) {
    return /*#__PURE__*/React.createElement(PurchaseOrderCard, {
      key: idx,
      order: order,
      lang: lang,
      link: createUrl(createDetailsPageUrl(order))
    });
  })))), resolvedTab === "car_services" && (carServiceOrders.length === 0 ? renderEmptyState((_data$car_services3 = data.car_services) === null || _data$car_services3 === void 0 ? void 0 : _data$car_services3.empty_state, emptyServiceImg) : /*#__PURE__*/React.createElement("div", {
    className: "py-6 flex flex-col gap-4"
  }, carServiceOrders.map(function (order) {
    return /*#__PURE__*/React.createElement(CarServiceCard, {
      key: order.order_id,
      order: order,
      link: createUrl("/tracking-service-details/".concat(order.order_id))
    });
  })))));
};
export default TrackingLandingTabs;